import { useTranslation } from 'react-i18next'
import type { ApiKey } from '@/api/catalogApi'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ListState, tdClass, thClass } from '@/components/admin/catalog/CatalogShared'
import { useKeyDeliveries } from '@/hooks/useCatalog'

/** The key's last 50 webhook deliveries: event, status, attempts, last error. Never payloads. */
export function KeyDeliveriesDialog({ apiKey, onClose }: { apiKey: ApiKey; onClose: () => void }) {
  const { t } = useTranslation()
  const deliveries = useKeyDeliveries(apiKey.id)
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('catalog.apiAccess.deliveriesTitle', { name: apiKey.name })}</DialogTitle>
        </DialogHeader>
        <ListState query={deliveries}>
          {deliveries.data?.length === 0 ? (
            <p className="text-body text-ink-muted">{t('catalog.apiAccess.noDeliveries')}</p>
          ) : (
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full table-fixed border-collapse">
                <thead className="bg-surface-subtle">
                  <tr>
                    <th className={thClass}>{t('catalog.apiAccess.deliveryCols.event')}</th>
                    <th className={`${thClass} w-24`}>{t('catalog.apiAccess.deliveryCols.status')}</th>
                    <th className={`${thClass} w-20`}>{t('catalog.apiAccess.deliveryCols.attempts')}</th>
                    <th className={`${thClass} w-44`}>{t('catalog.apiAccess.deliveryCols.when')}</th>
                    <th className={thClass}>{t('catalog.apiAccess.deliveryCols.error')}</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.data?.map((d) => (
                    <tr key={d.id} className="border-b border-line">
                      <td className={`${tdClass} font-mono text-label`}>{d.eventType}</td>
                      <td className={tdClass}>{d.status}</td>
                      <td className={`${tdClass} font-mono`}>{d.attempts}</td>
                      <td className={`${tdClass} text-label`}>{new Date(d.deliveredAt ?? d.createdAt).toLocaleString()}</td>
                      <td className={`${tdClass} break-all text-label text-ink-muted`}>{d.lastError ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ListState>
      </DialogContent>
    </Dialog>
  )
}
